// LLM generated - Model Claude 4 Sonnet
import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
  type ComponentProps,
  type PropsWithChildren,
} from 'react';

import createModalsContext from '~/domains/common/context/createModalsContext';
import { useIsTablet } from '~/domains/common/hooks/mediaQueries';

import { CHAT_PANEL_TOTAL_WIDTH, CHAT_PANEL_ANIMATION_DURATION } from '../constants/chatPanel';

import type ContestChatModal from './ContestChatModal';
import type PostModal from './PostModal';
import type ConfirmModal from './ConfirmModal';
import type ReportModal from './ReportModal';
import type DisableChatModal from './DisableChatModal';

// Define all modal types that can be opened within the Contest Chat system
type ContestChatModalType = {
  type: 'ContestChatModal';
} & Omit<ComponentProps<typeof ContestChatModal>, 'onClose' | 'isOpen'>;

type PostModalType = {
  type: 'PostModal';
} & Omit<ComponentProps<typeof PostModal>, 'onClose' | 'isOpen'>;

type ConfirmModalType = {
  type: 'ConfirmModal';
} & Omit<ComponentProps<typeof ConfirmModal>, 'onClose' | 'isOpen'>;

type ReportModalType = {
  type: 'ReportModal';
} & Omit<ComponentProps<typeof ReportModal>, 'onClose' | 'isOpen'>;

type DisableChatModalType = {
  type: 'DisableChatModal';
} & Omit<ComponentProps<typeof DisableChatModal>, 'onClose' | 'isOpen'>;

// Union of all modal types
type ModalType =
  | ContestChatModalType
  | PostModalType
  | ConfirmModalType
  | ReportModalType
  | DisableChatModalType;

// Create the base modal context
const { ModalsContext: BaseModalsContext, ModalsContextProvider: BaseModalsContextProvider } =
  createModalsContext<ModalType>();

// Extended context value with modal state management and chat panel state
type ExtendedContextValue = ReturnType<
  typeof createModalsContext<ModalType>
>['ModalsContext'] extends React.Context<infer T>
  ? T & {
      modalState: Record<string, any>;
      setModalState: (modalId: string, state: any) => void;
      clearModalState: (modalId: string) => void;
      isChatPanelOpen: boolean;
      isChatPanelClosing: boolean;
      chatOffset: number;
      chatPanelTotalWidth: number;
      openChatPanel: () => void;
      closeChatPanel: () => void;
      toggleChatPanel: () => void;
    }
  : never;

// Create extended context with state management
const ContestChatModalContext = React.createContext<ExtendedContextValue>({
  isCloseDisabled: false,
  modals: [],
  closeModal: () => {},
  closeModalsByType: () => {},
  disableClose: () => {},
  enableClose: () => {},
  openModal: () => Promise.resolve(),
  openModalUnique: () => Promise.resolve(),
  modalState: {},
  setModalState: () => {},
  clearModalState: () => {},
  isChatPanelOpen: false,
  isChatPanelClosing: false,
  chatOffset: 0,
  chatPanelTotalWidth: CHAT_PANEL_TOTAL_WIDTH,
  openChatPanel: () => {},
  closeChatPanel: () => {},
  toggleChatPanel: () => {},
});

// Custom provider that adds state management on top of base provider
function ContestChatModalContextProvider({ children }: PropsWithChildren) {
  const baseContext = React.useContext(BaseModalsContext);
  const isMobile = useIsTablet(); // Use tablet breakpoint to match header visibility logic
  const [modalState, setModalStateInternal] = useState<Record<string, any>>({});
  const [isChatPanelOpen, setIsChatPanelOpen] = useState(false);
  const [isChatPanelClosing, setIsChatPanelClosing] = useState(false);

  // Track previous isMobile value to detect viewport changes
  const prevIsMobileRef = useRef(isMobile);

  // Store stable reference to closeModalsByType to avoid dependency issues
  const closeModalsByTypeRef = useRef(baseContext.closeModalsByType);
  closeModalsByTypeRef.current = baseContext.closeModalsByType;

  // Ref to track close animation timeout for cleanup
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup timeout on unmount
  useEffect(
    () => () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    },
    []
  );

  useEffect(() => {
    const prevIsMobile = prevIsMobileRef.current;

    // Only trigger on actual viewport change (not initial mount)
    if (prevIsMobile === isMobile) {
      return;
    }

    prevIsMobileRef.current = isMobile;

    // When switching from mobile to desktop: close the mobile modal
    if (!isMobile && prevIsMobile) {
      closeModalsByTypeRef.current('ContestChatModal');
    }

    // When switching from desktop to mobile: close the side panel immediately
    if (isMobile && !prevIsMobile && isChatPanelOpen) {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
      }
      setIsChatPanelOpen(false);
      setIsChatPanelClosing(false);
    }
  }, [isMobile, isChatPanelOpen]);

  // Modal state management handlers
  const handleSetModalState = useCallback((modalId: string, state: any) => {
    setModalStateInternal((prev) => ({
      ...prev,
      [modalId]: state,
    }));
  }, []);

  const handleClearModalState = useCallback((modalId: string) => {
    setModalStateInternal((prev) => {
      const newState = { ...prev };
      delete newState[modalId];
      return newState;
    });
  }, []);

  // Chat panel state management - simplified to 2 states
  const openChatPanel = useCallback(() => {
    // Cancel any pending close animation
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setIsChatPanelClosing(false);
    setIsChatPanelOpen(true);
  }, []);

  const closeChatPanel = useCallback(() => {
    // Start closing animation
    setIsChatPanelClosing(true);
    // After animation completes, actually close the panel
    closeTimeoutRef.current = setTimeout(() => {
      setIsChatPanelOpen(false);
      setIsChatPanelClosing(false);
      closeTimeoutRef.current = null;
    }, CHAT_PANEL_ANIMATION_DURATION);
  }, []);

  const toggleChatPanel = useCallback(() => {
    if (isChatPanelOpen) {
      closeChatPanel();
    } else {
      openChatPanel();
    }
  }, [isChatPanelOpen, closeChatPanel, openChatPanel]);

  // Calculate chatOffset for responsive breakpoints:
  // - When open (not closing): apply offset so breakpoints adjust
  // - When closing: set to 0 so content expands with animation
  const chatOffset = isChatPanelOpen && !isChatPanelClosing ? CHAT_PANEL_TOTAL_WIDTH : 0;

  const extendedValue = useMemo(
    () => ({
      ...baseContext,
      modalState,
      setModalState: handleSetModalState,
      clearModalState: handleClearModalState,
      isChatPanelOpen,
      isChatPanelClosing,
      chatOffset,
      chatPanelTotalWidth: CHAT_PANEL_TOTAL_WIDTH,
      openChatPanel,
      closeChatPanel,
      toggleChatPanel,
    }),
    [
      baseContext,
      modalState,
      handleSetModalState,
      handleClearModalState,
      isChatPanelOpen,
      isChatPanelClosing,
      chatOffset,
      openChatPanel,
      closeChatPanel,
      toggleChatPanel,
    ]
  );

  return (
    <ContestChatModalContext.Provider value={extendedValue}>
      {children}
    </ContestChatModalContext.Provider>
  );
}

// Wrapper provider that includes base provider
function ContestChatModalContextProviderWrapper({ children }: PropsWithChildren) {
  return (
    <BaseModalsContextProvider>
      <ContestChatModalContextProvider>{children}</ContestChatModalContextProvider>
    </BaseModalsContextProvider>
  );
}

export {
  ContestChatModalContext,
  ContestChatModalContextProviderWrapper as ContestChatModalContextProvider,
};

export type { ModalType as ContestChatModalType };
